import { Form } from "@circle-react-uikit/Form";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { useSpaceGroupResource } from "@circle-react/contexts/useSpaceGroupResource";
import { useAddMembersToSpaceGroup } from "@circle-react/hooks/useAddMembersToSpaceGroup";
import { Modal } from "@circleco/compass/components/Modal";
import { useState } from "react";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import { AddMemberContentCompass } from "./AddMemberContentCompass";
import {
  ADD_SPACE_GROUP_MEMBERS_DEFAULT_VALUES,
  ADD_SPACE_GROUP_MEMBERS_FORM_ID,
  type AddSpaceGroupMemberContentCompassFormValues,
  type AddSpaceGroupMemberModalProps,
} from "./types";

export const AddSpaceGroupMemberComponent = ({
  queryKey,
  spaceGroupId,
}: AddSpaceGroupMemberModalProps) => {
  const [hasMembersAdded, setHasMembersAdded] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const modal = useModal();
  const { refetch } = useSpaceGroupResource(spaceGroupId);

  const { mutate: addMembers, isPending: isAdding } = useAddMembersToSpaceGroup(
    {
      onError: (error: { message: string }) => {
        toast.error(error.message);
      },
      onSuccess: ({ message }: { message: string }) => {
        if (queryKey) {
          void queryClient.invalidateQueries({ queryKey });
        }
        void queryClient.invalidateQueries({ queryKey: ["community-members"] });
        void refetch?.();
        toast.success(message, {
          duration: "short",
          shouldUseProgress: false,
        });
        void modal.hide();
      },
    },
  );

  const onSubmit = (formContent: AddSpaceGroupMemberContentCompassFormValues) =>
    addMembers({
      spaceGroupId,
      communityMembersId: formContent.community_member_ids ?? [],
      shouldAddAllMembers: formContent.add_all_members,
    });

  const primaryLabel = isAdding
    ? t("space_groups.invite.adding_to_space_group")
    : t("space_groups.invite.add_to_space_group");

  const handleOpenChange = (open: boolean) => {
    if (!open && modal.visible) void modal.hide();
  };

  return (
    <Modal.Root open={modal.visible} onOpenChange={handleOpenChange} size="md">
      <Modal.Content>
        <Modal.Header
          title={t("space_groups.form.edit.members.add_member")}
          showCloseButton
        />
        <Modal.Body>
          <Form
            id={ADD_SPACE_GROUP_MEMBERS_FORM_ID}
            mode="onChange"
            onSubmit={onSubmit}
            defaultValues={ADD_SPACE_GROUP_MEMBERS_DEFAULT_VALUES}
            className="flex flex-col"
            formClassName="flex flex-col"
          >
            <AddMemberContentCompass
              onHasMembersAddedChange={setHasMembersAdded}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
          }}
          primaryAction={{
            label: primaryLabel,
            type: "submit",
            form: ADD_SPACE_GROUP_MEMBERS_FORM_ID,
            disabled: isAdding || !hasMembersAdded,
            loading: isAdding,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
